/**
 * @typedef {Object} SuggestionObject
 * @property {string} id - Unique identifier for the suggestion.
 * @property {string} ruleName - Name of the rule that generated this suggestion.
 * @property {string} suggestionType - Type of suggestion (e.g., 'PLACE_LINK_OR_CREATE_FROM_ACCOMMODATION').
 * @property {string} status - Current status (e.g., 'pending', 'accepted', 'rejected', 'applied').
 * @property {Object} data - Payload containing all necessary data for applying the suggestion.
 * @property {Object} uiDetails - Information needed for rendering the suggestion in the UI.
 * @property {string} notificationStrategy - How the suggestion should be presented ('immediate_prompt', 'add_to_list').
 * @property {Object} originalEvent - The event data that triggered this suggestion.
 * @property {string} createdAt - ISO string timestamp of when the suggestion was created.
 */

import {
    EVENT_TYPES,
    eventBridgeService,
    getDay,
    updateDay,
    getPlaceById,
    createDayReferenceBlock,
    formatDayDisplayLabel,
    connectionRegistryService,
    linkRegistryConfigService,
    shouldRouteThroughRegistry,
    LINK_TYPES
} from '@trip-planner-monorepo/shared';
import { daysService } from '@trip-planner-monorepo/days';
import { placesService } from '@trip-planner-monorepo/places';
import { accommodationService, ACCOMMODATION_TYPES } from '@trip-planner-monorepo/accommodation';
import { suggestionRules } from './suggestionRules';
// Static imports for accommodation-day utilities and content block utilities
// NOTE: These were changed from dynamic imports to static imports to resolve HMR (Hot Module Replacement)
// conflicts during development. The original dynamic imports were causing module loading issues during
// hot reloads with errors like: "[HMR] unexpected require(...) from disposed module".
// 
// Static imports are more appropriate here because:
// 1. These utilities are always needed when processing accommodation-day suggestions
// 2. No performance benefit from lazy loading since they're used in core suggestion workflows  
// 3. Consistent with existing import patterns throughout the codebase
// 4. Eliminates HMR conflicts that were preventing development server from working properly
// 
// Dynamic imports should only be used for true code-splitting scenarios where modules are 
// conditionally loaded based on user actions (like in AddAccommodation.jsx for accommodation types).
import { accommodationDayUtils, contentBlockUtils } from '@trip-planner-monorepo/days';
const {
    calculateAccommodationDateRangeForDaysSuggestions,
    findMissingDaysForAccommodationDates,
    generateDayContentForAccommodationSuggestions,
    formatDayContentWithAccommodationEntry,
    determineAccommodationStayContext,
    calculateDaysListDateRange,
    formatDateForDisplay
} = accommodationDayUtils;
const { createContentBlock } = contentBlockUtils;

// Validate daysService import
console.log('SuggestionService: ES6 import of daysService successful:', !!daysService);
console.log('SuggestionService: daysService type:', typeof daysService);
console.log('SuggestionService: daysService methods:', daysService ? Object.keys(daysService) : 'none');

// Validate that required methods exist
if (!daysService || !daysService.getDayByDate || typeof daysService.getDayByDate !== 'function') {
    console.error('SuggestionService: CRITICAL ERROR - daysService.getDayByDate not available');
    console.error('SuggestionService: Available methods:', daysService ? Object.keys(daysService) : 'daysService is null/undefined');

    // TEMPORARY: Don't throw error to allow testing - just log the issue
    console.warn('SuggestionService: CONTINUING WITHOUT VALIDATION - accommodation-days integration may not work properly');
    // throw new Error('daysService.getDayByDate method not found - accommodation-days integration will not work');
}

console.log('SuggestionService: daysService successfully imported and validated');

// Basic utility for generating unique IDs (placeholder - consider a robust UUID library for production)
const generateUniqueId = () => {
    return `sugg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// Global state to prevent duplicate initialization and event processing across HMR reloads
if (!window.__suggestionServiceState) {
    window.__suggestionServiceState = {
        initialized: false,
        recentEvents: new Map() // Global event deduplication cache that survives HMR
    };
}

export const suggestionService = {
    pendingSuggestions: new Map(), // Internal map for 'add_to_list' strategy
    activeImmediateSuggestions: {}, // Cache for active immediate prompts { [suggestionId]: suggestionObject }
    _isInitialized: false, // Guard to prevent duplicate initialization
    _subscriptions: [], // Track subscriptions for cleanup

    async init() {
        // Prevent duplicate initialization during hot module replacement using global flag
        if (window.__suggestionServiceState.initialized) {
            console.log('SuggestionService: Already initialized globally (survives HMR), skipping duplicate initialization');
            return;
        }

        // Also check instance flag for additional safety
        if (this._isInitialized) {
            console.log('SuggestionService: Already initialized on instance, skipping duplicate initialization');
            return;
        }

        // Store subscription references for potential cleanup
        const accommodationEventsSubscription = eventBridgeService.subscribeToMany(
            [
                EVENT_TYPES.ACCOMMODATION_ADDED,
                EVENT_TYPES.ACCOMMODATION_UPDATED,
                EVENT_TYPES.ACCOMMODATION_CITY_CHANGED,
                EVENT_TYPES.ACCOMMODATION_DELETED
            ],
            this.handleEvent.bind(this)
        );
        this._subscriptions.push(accommodationEventsSubscription);
        console.log(`SuggestionService subscribed to accommodation events (ADDED, UPDATED, CITY_CHANGED, DELETED).`);

        // Add specific debugging for ACCOMMODATION_UPDATED events
        console.log(`DEBUG: SuggestionService specifically listening for ${EVENT_TYPES.ACCOMMODATION_UPDATED} events for Phase 2B date changes`);

        // Subscribe to user responses from immediate popups
        const userResponseSubscription = eventBridgeService.subscribe(EVENT_TYPES.USER_RESPONDED_TO_SUGGESTION, this.handleUserResponseEvent.bind(this));
        this._subscriptions.push(userResponseSubscription);
        console.log(`SuggestionService subscribed to ${EVENT_TYPES.USER_RESPONDED_TO_SUGGESTION} event.`);



        this._isInitialized = true;
        window.__suggestionServiceState.initialized = true; // Set global flag to survive HMR

        // Make debugging methods available in development mode
        if (process.env.NODE_ENV === 'development') {
            window.__tripPlannerDebug = {
                ...window.__tripPlannerDebug,
                suggestions: {
                    debugCache: () => this.debugActiveCache(),
                    clearStale: (minutes) => this.clearStaleActiveSuggestions(minutes),
                    clearAll: () => this.clearAllActiveSuggestions(),
                    debugAccommodation: (id) => this.debugAccommodationSuggestions(id),
                    forceRefresh: () => this.forceRefreshSuggestionCache()
                }
            };
            console.log('🔧 [SuggestionService] Debug methods available: window.__tripPlannerDebug.suggestions');
        }

        console.log('SuggestionService initialized and all relevant event subscriptions are active.');

        // Automatic startup cleanup - clear any stale suggestions from previous sessions
        setTimeout(() => {
            const clearedCount = this.automaticCacheCleanup();
            if (clearedCount > 0) {
                console.log(`🧹 [SuggestionService] Startup cleanup removed ${clearedCount} stale suggestions from previous session`);
            }
        }, 2000); // Wait 2 seconds after initialization

        // Add other subscriptions here as new rules and event interactions are defined, each with its own log.

        // Setup HMR cleanup if available
        if (module.hot) {
            module.hot.dispose(() => {
                console.log('SuggestionService: HMR dispose - cleaning up');
                this.cleanup();
                // Reset global flag so new module can initialize
                window.__suggestionServiceState.initialized = false;
            });
        }
    },

    // Cleanup method for HMR and testing
    cleanup() {
        console.log('SuggestionService: Cleaning up subscriptions');
        this._subscriptions.forEach(unsubscribe => {
            if (typeof unsubscribe === 'function') {
                try {
                    unsubscribe();
                } catch (error) {
                    console.warn('SuggestionService: Error during cleanup:', error);
                }
            }
        });
        this._subscriptions = [];
        // Note: We don't clear the global event cache as it should persist across HMR
        this._isInitialized = false;
        // Note: We don't reset the global flag as it should persist across HMR
    },

    async handleEvent(eventType, eventData) {
        console.log(`SuggestionService: Received event ${eventType}`, eventData);

        // Prevent duplicate event processing using GLOBAL event deduplication (survives HMR)
        const eventKey = `${eventType}_${eventData.id}_${eventData.updatedAt || eventData.createdAt}`;
        const now = Date.now();

        // Check if we've seen this exact event recently (within 2 seconds) - using global cache
        if (window.__suggestionServiceState.recentEvents.has(eventKey)) {
            const lastProcessed = window.__suggestionServiceState.recentEvents.get(eventKey);
            if (now - lastProcessed < 2000) {
                console.log(`SuggestionService: Duplicate event detected (${eventKey}), skipping processing`);
                return;
            }
        }

        // Cache this event in global cache
        window.__suggestionServiceState.recentEvents.set(eventKey, now);

        // Clean up old cache entries (older than 10 seconds) from global cache
        for (const [key, timestamp] of window.__suggestionServiceState.recentEvents.entries()) {
            if (now - timestamp > 10000) {
                window.__suggestionServiceState.recentEvents.delete(key);
            }
        }

        // Add extra debugging for city change events
        if (eventType === EVENT_TYPES.ACCOMMODATION_CITY_CHANGED) {
            console.log('SuggestionService: ACCOMMODATION_CITY_CHANGED event details:');
            console.log('- oldCity:', eventData.oldCity);
            console.log('- newCity:', eventData.details?.city);
            console.log('- oldPlaceReference:', eventData.oldPlaceReference);
        }

        // Add extra debugging for accommodation deletion events
        if (eventType === EVENT_TYPES.ACCOMMODATION_DELETED) {
            console.log('SuggestionService: ACCOMMODATION_DELETED event details:');
            console.log('- accommodationName:', eventData.summary || eventData.details?.name);
            console.log('- oldCity:', eventData.oldCity);
            console.log('- placeReference:', eventData.placeReference);
            console.log('- deletedAt:', eventData.deletedAt);
        }

        // Add specific debugging for ACCOMMODATION_UPDATED events (Phase 2B)
        if (eventType === EVENT_TYPES.ACCOMMODATION_UPDATED) {
            console.log('DEBUG: SuggestionService: ACCOMMODATION_UPDATED event received for Phase 2B date changes:');
            console.log('- accommodationId:', eventData.id);
            console.log('- hasOldAccommodation:', !!eventData.oldAccommodation);
            console.log('- hasNewAccommodation:', !!eventData.newAccommodation);
            console.log('- oldDates:', eventData.oldAccommodation?.details);
            console.log('- newDates:', eventData.newAccommodation?.details || eventData.details);

            if (eventData.oldAccommodation && (eventData.newAccommodation || eventData.details)) {
                const oldDates = eventData.oldAccommodation.details;
                const newDates = eventData.newAccommodation?.details || eventData.details;
                const oldCheckIn = oldDates?.checkInDate;
                const oldCheckOut = oldDates?.checkOutDate;
                const newCheckIn = newDates?.checkInDate;
                const newCheckOut = newDates?.checkOutDate;
                const datesChanged = oldCheckIn !== newCheckIn || oldCheckOut !== newCheckOut;

                console.log('DEBUG: Date change analysis:', {
                    oldCheckIn, newCheckIn, checkInChanged: oldCheckIn !== newCheckIn,
                    oldCheckOut, newCheckOut, checkOutChanged: oldCheckOut !== newCheckOut,
                    datesChanged
                });
            }
        }

        try {
            await this.generateSuggestions(eventData, eventType);
        } catch (error) {
            console.error(`SuggestionService: Error handling event ${eventType}:`, error, eventData);
        }
    },

    async generateSuggestions(eventData, eventType) {
        const applicableRules = suggestionRules.filter(rule => {
            // Handle both single event types and arrays of event types
            if (Array.isArray(rule.eventType)) {
                return rule.eventType.includes(eventType);
            }
            return rule.eventType === eventType;
        });
        console.log('SuggestionService: Applicable rules found:', applicableRules.length > 0 ? applicableRules.map(r => r.ruleName) : 'None');

        // AUTOMATIC CACHE CLEANUP: Clear stale suggestions before processing new ones
        this.automaticCacheCleanup();

        // AGGRESSIVE CLEANUP for accommodation updates - clear any existing suggestions for this accommodation
        if (eventType === EVENT_TYPES.ACCOMMODATION_UPDATED && eventData.id) {
            console.log(`🧹 [SuggestionService] ACCOMMODATION_UPDATED detected - aggressively clearing suggestions for accommodation ${eventData.id}`);

            // Force clear ALL suggestions for this accommodation, regardless of age or status
            const accommodationId = eventData.id;
            let aggressiveClearCount = 0;

            Object.keys(this.activeImmediateSuggestions).forEach(id => {
                const suggestion = this.activeImmediateSuggestions[id];
                if (suggestion.data?.accommodationId === accommodationId) {
                    console.log(`🗑️ [AGGRESSIVE CLEANUP] Removing suggestion: ${id} (${suggestion.ruleName}) - Status: ${suggestion.status}`);
                    delete this.activeImmediateSuggestions[id];
                    aggressiveClearCount++;
                }
            });

            console.log(`✅ [AGGRESSIVE CLEANUP] Cleared ${aggressiveClearCount} suggestions for accommodation ${accommodationId}. Cache now contains ${Object.keys(this.activeImmediateSuggestions).length} total suggestions.`);
        }

        const context = { // Context for rule functions
            placesService, // Make placesService available in the context
            accommodationService, // Make accommodationService available in the context
            daysService, // Make daysService available in the context
            // Potentially add other services or utilities here as needed by rules
        };

        for (const rule of applicableRules) {
            try {
                console.log(`SuggestionService: Evaluating conditions for rule "${rule.ruleName}"...`);
                // Inject eventType into eventData for rule logic
                const eventDataWithType = { ...eventData, eventType };
                if (await rule.conditions(eventDataWithType, context)) {
                    console.log(`SuggestionService: Conditions met for rule "${rule.ruleName}". Generating details...`);

                    // Enhanced duplicate prevention with automatic cleanup
                    const accommodationId = eventData.id;

                    // For accommodation updates, perform additional cleanup before checking duplicates
                    if (eventType === EVENT_TYPES.ACCOMMODATION_UPDATED) {
                        console.log(`🔍 [PRE-DUPLICATE-CHECK] Additional cleanup for accommodation ${accommodationId} before duplicate checking...`);

                        // Clear any stale or processed suggestions
                        let preCheckClearCount = 0;
                        Object.keys(this.activeImmediateSuggestions).forEach(id => {
                            const suggestion = this.activeImmediateSuggestions[id];
                            if (suggestion.data?.accommodationId === accommodationId &&
                                (['applied', 'accepted', 'rejected'].includes(suggestion.status) ||
                                    suggestion.ruleName === rule.ruleName)) {
                                console.log(`🗑️ [PRE-DUPLICATE-CHECK] Removing suggestion: ${id} (${suggestion.ruleName}) - Status: ${suggestion.status}`);
                                delete this.activeImmediateSuggestions[id];
                                preCheckClearCount++;
                            }
                        });

                        if (preCheckClearCount > 0) {
                            console.log(`✅ [PRE-DUPLICATE-CHECK] Cleared ${preCheckClearCount} additional suggestions for accommodation ${accommodationId}`);
                        }
                    }

                    const existingActiveSuggestion = Object.values(this.activeImmediateSuggestions).find(
                        s => s.data?.accommodationId === accommodationId &&
                            s.status === 'pending' &&
                            s.ruleName === rule.ruleName  // Only block exact same rule, allow different suggestion types
                    );

                    if (existingActiveSuggestion) {
                        console.log(`SuggestionService: Skipping duplicate suggestion for accommodation ${accommodationId}. Active suggestion ${existingActiveSuggestion.id} (${existingActiveSuggestion.ruleName}) already exists.`);
                        continue;
                    }

                    // Also check recently processed suggestions (to handle race conditions)
                    const recentlyProcessedCutoff = Date.now() - (5 * 1000); // 5 seconds ago
                    const recentSuggestion = Object.values(this.activeImmediateSuggestions).find(
                        s => s.data?.accommodationId === accommodationId &&
                            new Date(s.createdAt).getTime() > recentlyProcessedCutoff &&
                            (s.status === 'applied' || s.status === 'accepted')
                    );

                    if (recentSuggestion) {
                        console.log(`SuggestionService: Skipping duplicate suggestion for accommodation ${accommodationId}. Recent suggestion ${recentSuggestion.id} was processed ${((Date.now() - new Date(recentSuggestion.createdAt).getTime()) / 1000).toFixed(1)}s ago.`);
                        continue;
                    }

                    const details = await rule.generateSuggestionDetails(eventDataWithType, context);

                    // Rule can return null to indicate "don't generate a suggestion"
                    if (!details) {
                        console.log(`SuggestionService: Rule "${rule.ruleName}" returned null - skipping suggestion generation.`);
                        continue;
                    }

                    const suggestionId = generateUniqueId();
                    const newSuggestion = {
                        id: suggestionId,
                        ruleName: rule.ruleName,
                        suggestionType: details.suggestionType,
                        status: 'pending', // Initial status
                        data: details.data,
                        // Use uiDetails from the rule if provided, otherwise create basic fallback
                        uiDetails: details.uiDetails || {
                            title: details.uiTitle || `Suggestion from ${rule.ruleName}`, // Use uiTitle from details if provided
                            message: details.uiMessage || `Based on event ${eventType}, we suggest an action regarding: ${details.suggestionType}`, // Use uiMessage from details
                            // messageTemplateKey: details.messageTemplateKey, // For future localization
                        },
                        notificationStrategy: rule.notificationStrategy,
                        originalEvent: eventData,
                        createdAt: new Date().toISOString()
                    };

                    console.log('SuggestionService: New suggestion created:', newSuggestion);

                    // Check for duplicate orphaned place notifications before publishing
                    if (newSuggestion.suggestionType === 'PLACE_ORPHANED_NOTIFICATION') {
                        const placeId = newSuggestion.data.oldPlaceId || newSuggestion.data.placeId;
                        const existingOrphanedNotification = Object.values(this.activeImmediateSuggestions)
                            .find(s => s.suggestionType === 'PLACE_ORPHANED_NOTIFICATION' &&
                                (s.data.oldPlaceId === placeId || s.data.placeId === placeId));

                        if (existingOrphanedNotification) {
                            console.log(`SuggestionService: Duplicate orphaned place notification detected for place ${placeId}, skipping`);
                            continue; // Skip this duplicate notification
                        }
                    }

                    if (rule.notificationStrategy === 'immediate_prompt') {
                        console.log(`SuggestionService: Rule "${rule.ruleName}" indicates immediate_prompt. Caching and publishing SHOW_IMMEDIATE_SUGGESTION_POPUP.`);
                        this.activeImmediateSuggestions[newSuggestion.id] = newSuggestion; // Cache the suggestion
                        eventBridgeService.publish(EVENT_TYPES.SHOW_IMMEDIATE_SUGGESTION_POPUP, newSuggestion);
                    } else if (rule.notificationStrategy === 'add_to_list') {
                        // Placeholder for adding to pending list and notifying UI (Task P1.3)
                        console.log(`SuggestionService: Rule "${rule.ruleName}" indicates add_to_list. Adding to pendingSuggestions (pending full implementation).`);
                        this.pendingSuggestions.push(newSuggestion);
                        // eventBridgeService.publish(EVENT_TYPES.PENDING_SUGGESTIONS_UPDATED, this.pendingSuggestions);
                    }
                } else {
                    console.log(`SuggestionService: Conditions NOT met for rule "${rule.ruleName}".`);
                }
            } catch (error) {
                console.error(`SuggestionService: Error processing rule "${rule.ruleName}":`, error, eventData);
                // Optionally, handle rule-specific errors, e.g., by creating a 'failed_rule_evaluation' suggestion or logging to a monitoring service.
            }
        }
    },

    // Phase 2A: Accommodation-Days Integration Handler
    /**
     * Handles integration of accommodation entries into existing day entries.
     * 
     * This method processes suggestions to add accommodation information to specific
     * day entries that have been identified as suitable for accommodation content.
     * The process involves several key operations:
     * 
     * 1. Content Integration: Merges accommodation details with existing day content
     * 2. Duplicate Prevention: Ensures accommodation content isn't added multiple times
     * 3. Context Preservation: Maintains existing user-created day content
     * 4. Link Management: Updates accommodation-day relationship metadata
     * 5. Event Publishing: Notifies system of successful integrations
     * 
     * The method is designed to work with pre-validated day entries, meaning
     * the calling rule has already confirmed that the target days exist and
     * are appropriate for accommodation content addition.
     * 
     * Content integration uses specialized formatting utilities that preserve
     * user-written content while adding structured accommodation information
     * with appropriate context indicators (Check-in, Staying, Check-out).
     * 
     * @param {Object} suggestion - The day entries suggestion to process
     * @returns {Object} Processing result with success status and affected days
     */
    async syncAccommodationDayLinksToRegistry(userId, tripId, accommodationId, daysAffected) {
        if (!Array.isArray(daysAffected) || daysAffected.length === 0) {
            return { synced: 0, skipped: true, reason: 'no_days' };
        }

        const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
        if (!shouldRouteThroughRegistry(config)) {
            return { synced: 0, skipped: true, reason: 'registry_not_enabled' };
        }

        let synced = 0;
        const errors = [];

        for (const day of daysAffected) {
            if (!day?.dayId) {
                continue;
            }

            try {
                const result = await connectionRegistryService.createLink(
                    userId,
                    tripId,
                    { type: 'accommodation', id: accommodationId },
                    { type: 'day', id: day.dayId },
                    {
                        linkType: LINK_TYPES.STAYS_ON,
                        linkIntent: 'selected',
                        displayMode: 'itinerary_entry',
                        displayContext: day.context || null,
                        skipLegacyWrite: true,
                        metadata: {
                            createdBy: 'suggestion',
                            contentBlockId: day.contentBlockId || null
                        }
                    }
                );

                if (result.created || result.deduplicated) {
                    synced += 1;
                }
            } catch (error) {
                console.error(
                    '? [SuggestionService] syncAccommodationDayLinksToRegistry: Failed for day',
                    day.dayId,
                    error
                );
                errors.push({ dayId: day.dayId, error: error.message });
            }
        }

        return {
            synced,
            skipped: false,
            attempted: daysAffected.length,
            errors: errors.length > 0 ? errors : undefined
        };
    },

    /**
     * Archives the stays_on registry link for each dayId a stay no longer covers
     * (Issue 13). syncAccommodationDayLinksToRegistry only ever creates — a date
     * change that shortens or shifts a stay needs the removed nights' links archived
     * too, or the day itinerary keeps showing the hotel on nights it no longer covers.
     */
    async archiveAccommodationDayLinksInRegistry(userId, tripId, accommodationId, dayIds) {
        if (!Array.isArray(dayIds) || dayIds.length === 0) {
            return { archived: 0, skipped: true, reason: 'no_days' };
        }

        const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
        if (!shouldRouteThroughRegistry(config)) {
            return { archived: 0, skipped: true, reason: 'registry_not_enabled' };
        }

        let archived = 0;
        const errors = [];

        for (const dayId of dayIds) {
            if (!dayId) {
                continue;
            }
            try {
                const links = await connectionRegistryService.getLinksBetween(
                    userId,
                    tripId,
                    { type: 'accommodation', id: accommodationId },
                    { type: 'day', id: dayId },
                    LINK_TYPES.STAYS_ON
                );
                for (const link of links) {
                    await connectionRegistryService.removeLink(userId, tripId, link.linkId || link.id);
                    archived += 1;
                }
            } catch (error) {
                console.error(
                    '❌ [SuggestionService] archiveAccommodationDayLinksInRegistry: Failed for day',
                    dayId,
                    error
                );
                errors.push({ dayId, error: error.message });
            }
        }

        return {
            archived,
            skipped: false,
            attempted: dayIds.length,
            errors: errors.length > 0 ? errors : undefined
        };
    },

    async handleDayEntriesForAccommodation(suggestion) {
        console.log('🏨 [SuggestionService] handleDayEntriesForAccommodation: Starting unified accommodation day entry processing');

        try {
            // Validate that suggestion contains required day entry information
            if (!suggestion.data || !suggestion.data.dayEntries || !suggestion.data.accommodationId) {
                console.error('❌ [SuggestionService] handleDayEntriesForAccommodation: Missing required data');
                return {
                    success: false,
                    error: 'Missing required data: dayEntries or accommodationId',
                    processed: 0
                };
            }

            const {
                accommodationId,
                dayEntries,
                accommodationName,
                userId,
                tripId,
                isDateChangeEvent,
                operations,
                oldAccommodation
            } = suggestion.data;

            // Issue 20 / N5: on registry_only trips, the connection registry is the
            // source of truth for stays — writing day/accommodation content blocks too
            // just creates shadows Health Check later has to strip. Skip those legacy
            // writes here; syncAccommodationDayLinksToRegistry (below) still runs.
            let skipLegacyBlocks = false;
            try {
                const registryConfig = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
                skipLegacyBlocks = registryConfig.writeMode === 'registry_only';
            } catch (error) {
                console.warn('SuggestionService: could not read link registry config; defaulting to legacy writes', error);
            }

            console.log(`🏨 [SuggestionService] handleDayEntriesForAccommodation: Processing ${dayEntries.length} day entries for accommodation "${accommodationName}"`);
            console.log(`🏨 [SuggestionService] handleDayEntriesForAccommodation: Is date change event: ${isDateChangeEvent}`);
            console.log(`🏨 [SuggestionService] handleDayEntriesForAccommodation: Operations to perform: ${operations?.length || 0}`);

            // Using static imports for Phase 2B structured content utilities
            // NOTE: These were previously dynamic imports but were causing HMR conflicts during development

            let totalProcessed = 0;
            const results = [];

            // Step 1: Handle removal operations for date changes
            if (isDateChangeEvent && operations) {
                const removeOperation = operations.find(op => op.type === 'REMOVE_OLD_ENTRIES');
                if (removeOperation && oldAccommodation) {
                    console.log('🗑️ [SuggestionService] handleDayEntriesForAccommodation: Removing old day entries for date change');

                    const removeResult = await this.removeAccommodationContentBlocks(
                        userId,
                        tripId,
                        accommodationId,
                        oldAccommodation
                    );

                    results.push({
                        operation: 'REMOVE_OLD_ENTRIES',
                        ...removeResult
                    });

                    totalProcessed += removeResult.processed || 0;
                    console.log(`🗑️ [SuggestionService] handleDayEntriesForAccommodation: Removed ${removeResult.processed || 0} old content blocks`);
                }
            }

            // Get accommodation details for content block creation
            const accommodation = await accommodationService.getAccommodationById(userId, tripId, accommodationId);
            if (!accommodation) {
                throw new Error('Accommodation not found');
            }

            // Calculate date range for context determination
            const dateRange = calculateAccommodationDateRangeForDaysSuggestions(accommodation);

            let processed = 0;
            let errors = [];
            const daysAffected = [];

            // Process each day entry using Phase 2B content blocks
            for (const dayEntry of dayEntries) {
                try {
                    console.log(`🏨 [SuggestionService] handleDayEntriesForAccommodation: Processing day ${dayEntry.date} (${dayEntry.dayId})`);

                    // Determine the context for this date
                    const context = determineAccommodationStayContext(accommodation.details, dayEntry.date, dateRange.dates);

                    // Create accommodation content block with proper context
                    const placeName = accommodation.details?.city || '';

                    // Format display text based on context
                    // Use only the accommodation name (not summary which includes place name)
                    const accommodationNameOnly = accommodation.details?.name || 'Accommodation';
                    let displayText = `Stay at ${accommodationNameOnly}`;
                    if (placeName) {
                        displayText += `: ${placeName}`;
                    }

                    // Add context indicators
                    switch (context) {
                        case 'check-in':
                            displayText += ' (Check-in)';
                            break;
                        case 'check-out':
                            displayText += ' (Check-out)';
                            break;
                        case 'single-day':
                            // No additional indicator for single day stays
                            break;
                        case 'staying':
                        default:
                            // No additional indicator for regular staying days
                            break;
                    }

                    const contentBlock = createContentBlock('accommodation_reference', {
                        accommodationId,
                        accommodationName: accommodationNameOnly,
                        placeName,
                        placeId: accommodation.placeReference?.placeId || null,
                        context,
                        displayText
                    });

                    console.log(`🏨 [SuggestionService] handleDayEntriesForAccommodation: Created content block for ${dayEntry.date}:`, {
                        contentBlockId: contentBlock.id,
                        context,
                        displayText
                    });

                    // Add content block to day using Phase 2B system — skipped on
                    // registry_only trips (Issue 20); the registry sync below is what
                    // actually persists the stay there.
                    const addResult = skipLegacyBlocks
                        ? { success: true }
                        : await daysService.addAccommodationContentBlock(
                            userId,
                            tripId,
                            dayEntry.dayId,
                            contentBlock
                        );

                    if (addResult.success) {
                        processed++;
                        daysAffected.push({
                            date: dayEntry.date,
                            dayId: dayEntry.dayId,
                            contentBlockId: contentBlock.id,
                            context
                        });
                        console.log(`✅ [SuggestionService] handleDayEntriesForAccommodation: Successfully added content block to day ${dayEntry.date}`);
                    } else {
                        errors.push({
                            date: dayEntry.date,
                            dayId: dayEntry.dayId,
                            error: addResult.error || 'Failed to add content block'
                        });
                        console.error(`❌ [SuggestionService] handleDayEntriesForAccommodation: Failed to add content block to day ${dayEntry.date}:`, addResult.error);
                    }

                } catch (dayError) {
                    console.error(`❌ [SuggestionService] handleDayEntriesForAccommodation: Error processing day ${dayEntry.date}:`, dayError);
                    errors.push({
                        date: dayEntry.date,
                        dayId: dayEntry.dayId,
                        error: dayError.message
                    });
                }
            }

            // Publish event to notify system of successful accommodation-day integration
            if (processed > 0) {
                eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_DAY_LINK_CREATED, {
                    userId,
                    tripId,
                    accommodationId,
                    accommodationName,
                    daysAffected: daysAffected.map(day => ({
                        date: day.date,
                        dayId: day.dayId,
                        contentBlockId: day.contentBlockId,
                        context: day.context
                    })),
                    dayLinksCreated: daysAffected.length,
                    contentBlocksCreated: daysAffected.length,
                    timestamp: new Date().toISOString(),
                    eventId: `evt_${Date.now()}`
                });

                console.log(`📡 [SuggestionService] handleDayEntriesForAccommodation: Published ACCOMMODATION_DAY_LINK_CREATED event`);
            }

            // Add the addition operation result
            totalProcessed += processed;
            results.push({
                operation: 'ADD_NEW_ENTRIES',
                success: errors.length === 0,
                processed,
                errors: errors.length > 0 ? errors : undefined
            });

            // Compile comprehensive result information
            const overallSuccess = results.every(r => r.success);
            const result = {
                success: overallSuccess,
                processed: totalProcessed,
                daysAffected: daysAffected.map(day => ({
                    date: day.date,
                    dayId: day.dayId,
                    contentBlockId: day.contentBlockId,
                    context: day.context
                })),
                totalAttempted: dayEntries.length,
                operations: results,
                isDateChangeEvent: isDateChangeEvent || false
            };

            if (errors.length > 0) {
                result.partialSuccess = processed > 0;
                result.errors = errors;
                result.failedDays = errors.map(err => err.date);

                // If all addition operations failed, provide clear error message
                if (processed === 0) {
                    result.error = 'Failed to integrate accommodation into any day entries';
                    console.error('❌ [SuggestionService] handleDayEntriesForAccommodation: All day integration operations failed');
                }
            }

            const operationSummary = isDateChangeEvent
                ? `date change processing: ${totalProcessed} total operations (${results.length} operation types)`
                : `day entry addition: ${processed} successful, ${errors.length} failed`;

            console.log(`✅ [SuggestionService] handleDayEntriesForAccommodation: ${operationSummary}`);

            // Update accommodation contentBlocks with bidirectional references — skipped
            // on registry_only trips (Issue 20): the registry sync below is what
            // actually persists the accommodation<->day relationship there. The registry
            // sync itself must stay unconditional, so this only guards the legacy write.
            if (daysAffected.length > 0 && !skipLegacyBlocks) {
                try {
                    console.log(`🔗 [SuggestionService] handleDayEntriesForAccommodation: Updating accommodation contentBlocks with ${daysAffected.length} day references`);

                    // CRITICAL FIX: First fetch existing contentBlocks to preserve document_reference blocks
                    // The previous implementation was overwriting all contentBlocks with just the new ones
                    const existingContentData = await accommodationService.getContentBlocks(userId, tripId, accommodationId);
                    const existingContentBlocks = existingContentData.contentBlocks || [];

                    console.log(`🔗 [SuggestionService] handleDayEntriesForAccommodation: Found ${existingContentBlocks.length} existing content blocks to preserve`);

                    const tripDays = await daysService.fetchDays(userId, tripId);
                    const dayById = new Map(tripDays.map((tripDay) => [tripDay.id, tripDay]));

                    const newContentBlockReferences = daysAffected.map((day) =>
                        this.buildAccommodationDayReferenceBlock({
                            ...day,
                            day: dayById.get(day.dayId)
                        })
                    );

                    // Merge existing blocks (document_reference, etc.) with new accommodation_reference blocks
                    const mergedContentBlocks = [...existingContentBlocks, ...newContentBlockReferences];

                    console.log(`🔗 [SuggestionService] handleDayEntriesForAccommodation: Merging ${existingContentBlocks.length} existing + ${newContentBlockReferences.length} new = ${mergedContentBlocks.length} total blocks`);

                    // accommodationService is imported as ESM import at top of file
                    await accommodationService.updateContentBlocks(
                        userId,
                        tripId,
                        accommodationId,
                        mergedContentBlocks
                    );

                    console.log(`✅ [SuggestionService] handleDayEntriesForAccommodation: Updated accommodation contentBlocks successfully`);
                } catch (error) {
                    console.error(`❌ [SuggestionService] handleDayEntriesForAccommodation: Failed to update accommodation contentBlocks:`, error);
                    // Don't fail the entire operation if contentBlocks update fails
                    result.warnings = result.warnings || [];
                    result.warnings.push(`Failed to update accommodation contentBlocks: ${error.message}`);
                }
            }

            // Registry sync is unconditional — this is what actually persists the stay
            // on registry_only trips, where the legacy write above was just skipped.
            if (daysAffected.length > 0) {
                const registrySync = await this.syncAccommodationDayLinksToRegistry(
                    userId,
                    tripId,
                    accommodationId,
                    daysAffected
                );
                result.registrySync = registrySync;

                if (registrySync.errors?.length) {
                    result.warnings = result.warnings || [];
                    result.warnings.push(
                        `Registry sync had ${registrySync.errors.length} error(s) for accommodation-day links`
                    );
                } else if (!registrySync.skipped && registrySync.synced > 0) {
                    console.log(
                        `? [SuggestionService] handleDayEntriesForAccommodation: Synced ${registrySync.synced} link(s) to connection registry`
                    );
                }
            }

            return result;

        } catch (error) {
            console.error('❌ [SuggestionService] handleDayEntriesForAccommodation: Critical error:', error);
            return {
                success: false,
                error: error.message,
                processed: 0,
                totalAttempted: suggestion.data?.dayEntries?.length || 0
            };
        }
    },

    /**
     * Determines the contextual role of a day within an accommodation stay period.
     * 
     * This helper method analyzes the position of a specific date within the
     * accommodation's stay timeline to determine what type of context indicator
     * should be associated with the day link metadata.
     * 
     * @param {string} date - The date to analyze (ISO date string)
     * @param {Object} dateRange - The accommodation's date range information
     * @returns {string} Context type: 'check-in', 'staying', 'check-out', or 'single-day'
     */
    determineDayContext(date, dateRange) {
        if (!dateRange || !date) {
            return 'staying';
        }

        // Handle single-day stays
        if (dateRange.startDate === dateRange.endDate) {
            return 'single-day';
        }

        // Determine context based on position in stay
        if (date === dateRange.startDate) {
            return 'check-in';
        } else if (date === dateRange.endDate) {
            return 'check-out';
        } else {
            return 'staying';
        }
    },

    /**
     * Builds a typed day_reference block for accommodation contentBlocks.
     * Preserves contentBlockId and context for bidirectional day-side cleanup.
     */
    buildAccommodationDayReferenceBlock({ dayId, contentBlockId, date, context, day }) {
        const dayName = day
            ? formatDayDisplayLabel(day)
            : (formatDateForDisplay(date) || date || 'Day');
        return createDayReferenceBlock(dayId, dayName, date, {
            contentBlockId,
            context
        });
    },

    /**
     * Resolves the day-side content block id from legacy or typed accommodation blocks.
     */
    getDaySideContentBlockId(block) {
        return block?.contentBlockId || null;
    },

    // Phase 2B: Accommodation Date Changes Handler
    async handleDayUpdatesForChangedAccommodation(suggestion) {
        try {
            console.log('SuggestionService: Processing DAY_UPDATES_FOR_CHANGED_ACCOMMODATION suggestion:', suggestion.id);

            // Validate suggestion data
            if (!suggestion.data || !suggestion.data.suggestions || !suggestion.data.changeAnalysis) {
                return {
                    success: false,
                    error: 'Missing required data: suggestions or changeAnalysis'
                };
            }

            const {
                accommodationId,
                suggestions,
                changeAnalysis,
                newDetails,
                userId,
                tripId
            } = suggestion.data;

            let totalProcessed = 0;
            let totalErrors = [];
            const results = [];
            const daysAffected = new Set();
            const addedDayEntries = []; // {dayId, context, contentBlockId} — registry stays_on to create
            const removedDayIds = []; // dayIds — registry stays_on to archive

            console.log(`Processing ${suggestions.length} day update operations for accommodation ${accommodationId}`);

            // Process each suggestion in sequence
            for (const updateSuggestion of suggestions) {
                try {
                    let operationResult;

                    switch (updateSuggestion.type) {
                        case 'ADD_DAY_ENTRIES':
                            operationResult = await this.processAddDayEntries(
                                updateSuggestion, accommodationId, newDetails, userId, tripId
                            );
                            break;

                        case 'REMOVE_DAY_ENTRIES':
                            operationResult = await this.processRemoveDayEntries(
                                updateSuggestion, accommodationId, userId, tripId
                            );
                            break;

                        case 'UPDATE_DAY_ENTRIES':
                            operationResult = await this.processUpdateDayEntries(
                                updateSuggestion, accommodationId, newDetails, userId, tripId
                            );
                            break;

                        default:
                            console.warn(`Unknown update suggestion type: ${updateSuggestion.type}`);
                            operationResult = {
                                success: false,
                                error: `Unknown operation type: ${updateSuggestion.type}`,
                                processed: 0
                            };
                    }

                    // Track results
                    results.push({
                        type: updateSuggestion.type,
                        ...operationResult
                    });

                    totalProcessed += operationResult.processed || 0;

                    if (operationResult.errors) {
                        totalErrors.push(...operationResult.errors);
                    }

                    // Track affected days
                    if (operationResult.daysAffected) {
                        operationResult.daysAffected.forEach(day => daysAffected.add(day));
                    }

                    // Track registry-sync targets separately (real dayIds only — Issue 13)
                    if (updateSuggestion.type === 'ADD_DAY_ENTRIES' && operationResult.resolvedDayEntries) {
                        addedDayEntries.push(...operationResult.resolvedDayEntries);
                    }
                    if (updateSuggestion.type === 'REMOVE_DAY_ENTRIES' && operationResult.resolvedDayIds) {
                        removedDayIds.push(...operationResult.resolvedDayIds);
                    }

                } catch (error) {
                    console.error(`Error processing ${updateSuggestion.type} operation:`, error);
                    totalErrors.push({
                        operation: updateSuggestion.type,
                        error: error.message
                    });
                }
            }

            // Update accommodation-day links if we have accommodation service available
            try {
                if (accommodationService.updateAccommodationDayLinks) {
                    const finalDayIds = Array.from(daysAffected);
                    await accommodationService.updateAccommodationDayLinks(
                        userId, tripId, accommodationId, finalDayIds
                    );
                }
            } catch (error) {
                console.warn('Could not update accommodation-day links:', error.message);
            }

            // Sync the connection registry to match (Issue 13). The legacy content-block
            // add/remove above has no registry counterpart on its own — without this,
            // a shortened or shifted stay leaves stale stays_on rows for nights the hotel
            // no longer covers, and new nights never get one at all.
            const registrySync = { addedSync: null, removedSync: null };
            try {
                if (addedDayEntries.length > 0) {
                    registrySync.addedSync = await this.syncAccommodationDayLinksToRegistry(
                        userId, tripId, accommodationId, addedDayEntries
                    );
                }
                if (removedDayIds.length > 0) {
                    registrySync.removedSync = await this.archiveAccommodationDayLinksInRegistry(
                        userId, tripId, accommodationId, removedDayIds
                    );
                }
            } catch (error) {
                console.warn('Could not sync accommodation-day registry links:', error.message);
                totalErrors.push({ operation: 'registry_sync', error: error.message });
            }

            // Publish appropriate events based on the change type
            this.publishDateChangeEvents(changeAnalysis, accommodationId, userId, tripId, Array.from(daysAffected));

            const overallResult = {
                success: totalErrors.length === 0,
                changeType: changeAnalysis.type,
                totalProcessed,
                daysAffected: Array.from(daysAffected),
                operationResults: results,
                registrySync
            };

            if (totalErrors.length > 0) {
                overallResult.partialSuccess = totalProcessed > 0;
                overallResult.errors = totalErrors;
            }

            console.log(`Completed day updates for accommodation ${accommodationId}:`, overallResult);
            return overallResult;

        } catch (error) {
            console.error('SuggestionService: Error in handleDayUpdatesForChangedAccommodation:', error);
            return {
                success: false,
                error: error.message,
                processed: 0
            };
        }
    },

    /**
     * Handle simplified accommodation date changes using delete + create approach
     * This replaces the complex modification logic with simple operations
     * Updated to use content blocks system for precise content management
     */


    /**
     * Remove accommodation content blocks from all days that reference it
     * Uses content block IDs for precise removal
     */
    async removeAccommodationContentBlocks(userId, tripId, accommodationId, accommodation) {
        console.log(`🗑️ [SuggestionService] removeAccommodationContentBlocks: Starting cleanup operation`, {
            accommodationId,
            hasAccommodation: !!accommodation,
            contentBlocksCount: accommodation?.details?.contentBlocks?.length || 0
        });

        try {
            // If no old accommodation data provided, cannot determine which days to clean
            if (!accommodation || !accommodation.details) {
                console.log(`🔍 [SuggestionService] removeAccommodationContentBlocks: No old accommodation data provided, cannot determine days to clean`);
                return { success: true, processed: 0, message: 'No old accommodation data to determine cleanup' };
            }

            // Using static imports for date processing utilities
            // NOTE: Previously used dynamic imports but changed to static for HMR stability

            // Calculate which dates the OLD accommodation covered
            const oldDateRange = calculateAccommodationDateRangeForDaysSuggestions(accommodation);
            if (!oldDateRange || !oldDateRange.dates || oldDateRange.dates.length === 0) {
                console.log(`🔍 [SuggestionService] removeAccommodationContentBlocks: No valid date range for old accommodation`);
                return { success: true, processed: 0, message: 'No valid date range to clean' };
            }

            console.log(`🔍 [SuggestionService] removeAccommodationContentBlocks: Old accommodation covered dates:`, oldDateRange.dates);

            let processed = 0;
            const errors = [];

            // First, try to get content block references from accommodation service (new approach)
            const contentBlockData = await accommodationService.getContentBlocks(userId, tripId, accommodationId);
            const storedContentBlocks = contentBlockData.contentBlocks || [];

            console.log(`🔍 [SuggestionService] removeAccommodationContentBlocks: Found ${storedContentBlocks.length} stored content block references`);

            // Remove stored content blocks first (precise removal)
            for (const contentBlockRef of storedContentBlocks) {
                const daySideContentBlockId = this.getDaySideContentBlockId(contentBlockRef);
                if (!contentBlockRef.dayId || !daySideContentBlockId) {
                    continue;
                }

                try {
                    const removeResult = await daysService.removeContentBlock(
                        userId,
                        tripId,
                        contentBlockRef.dayId,
                        daySideContentBlockId
                    );

                    if (removeResult.success) {
                        processed++;
                        console.log(`🗑️ [SuggestionService] removeAccommodationContentBlocks: Removed stored content block ${daySideContentBlockId} from day ${contentBlockRef.dayId}`);
                    } else {
                        console.log(`🔍 [SuggestionService] removeAccommodationContentBlocks: Stored content block ${daySideContentBlockId} not found (may have been already removed)`);
                    }
                } catch (error) {
                    console.error(`🗑️ [SuggestionService] removeAccommodationContentBlocks: Error removing stored content block ${daySideContentBlockId}:`, error);
                }
            }

            // Also remove legacy accommodation content from each day (fallback approach)
            // Use only the accommodation name (not summary which includes place name)
            const accommodationName = accommodation.details?.name || 'Hotel';

            for (const date of oldDateRange.dates) {
                try {
                    // Find the day by date
                    const existingDay = await daysService.getDayByDate(userId, tripId, date);
                    if (!existingDay) {
                        console.log(`🔍 [SuggestionService] removeAccommodationContentBlocks: No day found for date ${date}, skipping cleanup`);
                        continue;
                    }

                    // First, force conversion of legacy content to structured content blocks if needed
                    const isStructured = await daysService.isContentStructured(userId, tripId, existingDay.id);
                    if (!isStructured) {
                        console.log(`🔄 [SuggestionService] removeAccommodationContentBlocks: Converting legacy content to structured for day ${existingDay.id} (${date})`);
                        await daysService.convertLegacyContentToStructured(userId, tripId, existingDay.id);
                    }

                    // Remove accommodation content blocks that reference this accommodation
                    const removeResult = await daysService.removeAccommodationContentBlocks(
                        userId,
                        tripId,
                        existingDay.id,
                        accommodationId
                    );

                    if (removeResult.success && removeResult.removedCount > 0) {
                        processed += removeResult.removedCount;
                        console.log(`🗑️ [SuggestionService] removeAccommodationContentBlocks: Removed ${removeResult.removedCount} accommodation content blocks from day ${existingDay.id} (${date})`);
                    }

                } catch (error) {
                    console.error(`🗑️ [SuggestionService] removeAccommodationContentBlocks: Error cleaning day ${date}:`, error);
                    errors.push({
                        date,
                        error: error.message
                    });
                }
            }

            // Clear content block references from accommodation
            await accommodationService.clearContentBlockReferences(userId, tripId, accommodationId);

            return {
                success: errors.length === 0,
                processed,
                errors,
                message: `🗑️ [SuggestionService] removeAccommodationContentBlocks: Removed ${processed} accommodation content blocks`
            };

        } catch (error) {
            console.error('🗑️ [SuggestionService] removeAccommodationContentBlocks: Error in removeAccommodationContentBlocks:', error);
            return {
                success: false,
                error: error.message,
                processed: 0
            };
        }
    },

    /**
     * Add accommodation content blocks to appropriate days
     * Ensures accommodation entries are added as the last content block
     */
    async addAccommodationContentBlocks(userId, tripId, accommodationId, accommodation) {
        console.log(`🏨 [SuggestionService] addAccommodationContentBlocks: Adding accommodation content blocks for ${accommodationId}`);

        try {
            // Using static imports for date processing and content block utilities
            // NOTE: Previously used dynamic imports but changed to static for HMR stability

            // Fetch current accommodation data to ensure we have the latest placeReference
            // This fixes the issue where placeId was null in content blocks during date changes
            console.log(`🔍 [SuggestionService] addAccommodationContentBlocks: About to fetch fresh accommodation data for ${accommodationId}`);
            let currentAccommodation = accommodation;

            try {
                console.log(`🔍 [SuggestionService] addAccommodationContentBlocks: Calling accommodationService.getAccommodationById(${userId}, ${tripId}, ${accommodationId})`);
                const freshAccommodationData = await accommodationService.getAccommodationById(userId, tripId, accommodationId);
                console.log(`🔍 [SuggestionService] addAccommodationContentBlocks: Fresh data fetch result:`, {
                    hasFreshData: !!freshAccommodationData,
                    hasPlaceReference: !!freshAccommodationData?.placeReference,
                    placeId: freshAccommodationData?.placeReference?.placeId || 'null'
                });

                if (freshAccommodationData) {
                    currentAccommodation = freshAccommodationData;
                    console.log(`🔄 [SuggestionService] addAccommodationContentBlocks: Using fresh accommodation data with placeId: ${freshAccommodationData.placeReference?.placeId || 'null'}`);
                } else {
                    console.warn(`⚠️ [SuggestionService] addAccommodationContentBlocks: Could not fetch fresh accommodation data, using provided data`);
                }
            } catch (fetchError) {
                console.error(`⚠️ [SuggestionService] addAccommodationContentBlocks: Error fetching fresh accommodation data:`, fetchError);
                // Continue with provided accommodation data
            }

            // Calculate which dates the accommodation should cover
            const dateRange = calculateAccommodationDateRangeForDaysSuggestions(currentAccommodation);
            console.log(`🏨 [SuggestionService] addAccommodationContentBlocks: Accommodation covers dates:`, dateRange.dates);

            let processed = 0;
            const errors = [];
            const addedContentBlocks = [];

            // Add accommodation content block to each day
            for (const date of dateRange.dates) {
                try {
                    // Find the day by date
                    const existingDay = await daysService.getDayByDate(userId, tripId, date);
                    if (!existingDay) {
                        console.log(`🏨 [SuggestionService] addAccommodationContentBlocks: No day found for date ${date}, skipping`);
                        continue;
                    }

                    // Determine the context for this date
                    const context = determineAccommodationStayContext(currentAccommodation.details, date, dateRange.dates);

                    // Create accommodation content block
                    // Use only the accommodation name (not summary which includes place name)
                    const accommodationName = currentAccommodation.details?.name || 'Accommodation';
                    const placeName = currentAccommodation.details?.city || '';

                    // Format display text based on context
                    let displayText = `Stay at ${accommodationName}`;
                    if (placeName) {
                        displayText += `: ${placeName}`;
                    }

                    // Add context indicators
                    switch (context) {
                        case 'check-in':
                            displayText += ' (Check-in)';
                            break;
                        case 'check-out':
                            displayText += ' (Check-out)';
                            break;
                        case 'single-day':
                            // No additional indicator for single day stays
                            break;
                        case 'staying':
                        default:
                            // No additional indicator for regular staying days
                            break;
                    }

                    const contentBlock = createContentBlock('accommodation_reference', {
                        accommodationId,
                        accommodationName,
                        placeName,
                        placeId: currentAccommodation.placeReference?.placeId || null,
                        context,
                        displayText
                    });

                    // Add accommodation content block to day (this ensures it goes to the last line)
                    const addResult = await daysService.addAccommodationContentBlock(userId, tripId, existingDay.id, contentBlock);

                    if (addResult.success) {
                        processed++;
                        addedContentBlocks.push(
                            this.buildAccommodationDayReferenceBlock({
                                dayId: existingDay.id,
                                contentBlockId: contentBlock.id,
                                date,
                                context,
                                day: existingDay
                            })
                        );
                        console.log(`🏨 [SuggestionService] addAccommodationContentBlocks: Added content block ${contentBlock.id} to day ${existingDay.id} for date ${date}`);
                    } else {
                        errors.push({
                            date,
                            dayId: existingDay.id,
                            error: 'Failed to add content block'
                        });
                    }

                } catch (error) {
                    console.error(`🏨 [SuggestionService] addAccommodationContentBlocks: Error adding content block for date ${date}:`, error);
                    errors.push({
                        date,
                        error: error.message
                    });
                }
            }

            // Update accommodation with content block references
            // CRITICAL FIX: Merge with existing contentBlocks to preserve document_reference blocks
            if (addedContentBlocks.length > 0) {
                const existingContentData = await accommodationService.getContentBlocks(userId, tripId, accommodationId);
                const existingContentBlocks = existingContentData.contentBlocks || [];
                const mergedContentBlocks = [...existingContentBlocks, ...addedContentBlocks];
                console.log(`🔗 [SuggestionService] addAccommodationContentBlocks: Merging ${existingContentBlocks.length} existing + ${addedContentBlocks.length} new = ${mergedContentBlocks.length} total blocks`);
                await accommodationService.updateContentBlocks(userId, tripId, accommodationId, mergedContentBlocks);
            }

            return {
                success: errors.length === 0,
                processed,
                errors,
                addedContentBlocks,
                message: `🏨 [SuggestionService] addAccommodationContentBlocks: Added ${processed} accommodation content blocks`
            };

        } catch (error) {
            console.error('🏨 [SuggestionService] addAccommodationContentBlocks: Error in addAccommodationContentBlocks:', error);
            return {
                success: false,
                error: error.message,
                processed: 0
            };
        }
    },

    // Helper method for adding day entries
    async processAddDayEntries(updateSuggestion, accommodationId, accommodationDetails, userId, tripId) {
        const { days, content } = updateSuggestion;
        let processed = 0;
        let errors = [];
        const daysAffected = [];
        const resolvedDayEntries = []; // {dayId, context, contentBlockId} — for registry sync (Issue 13)

        for (const day of days) {
            try {
                // Check if 'day' is a date string (YYYY-MM-DD) or already a dayId
                let dayId = day;

                // If it looks like a date string, convert to dayId by looking up the day
                if (typeof day === 'string' && day.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    console.log(`🏨 [SuggestionService] processAddDayEntries: Converting date ${day} to dayId`);
                    const existingDay = await daysService.getDayByDate(userId, tripId, day);
                    if (existingDay) {
                        dayId = existingDay.id;
                        console.log(`🏨 [SuggestionService] processAddDayEntries: Found dayId ${dayId} for date ${day}`);
                    } else {
                        console.warn(`🏨 [SuggestionService] processAddDayEntries: No day found for date ${day}, skipping`);
                        errors.push({ date: day, error: `No day entry exists for date ${day}` });
                        continue;
                    }
                }

                // Create structured accommodation content block
                const dateRange = {
                    startDate: accommodationDetails?.checkInDate,
                    endDate: accommodationDetails?.checkOutDate
                };

                const accommodationContentBlock = {
                    id: `acc_block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    type: 'accommodation_reference',
                    accommodationId: accommodationId,
                    accommodationName: accommodationDetails?.name || 'Accommodation',
                    context: this.determineDayContext(day, dateRange),
                    createdAt: new Date().toISOString()
                };

                const result = await daysService.addAccommodationContentBlock(
                    userId, tripId, dayId, accommodationContentBlock
                );

                if (result.success) {
                    processed++;
                    daysAffected.push(day); // Keep original day identifier for tracking
                    resolvedDayEntries.push({
                        dayId,
                        context: accommodationContentBlock.context,
                        contentBlockId: accommodationContentBlock.id
                    });
                } else {
                    errors.push({ date: day, error: result.error });
                }
            } catch (error) {
                console.error(`🏨 [SuggestionService] processAddDayEntries: Error processing day ${day}:`, error);
                errors.push({ date: day, error: error.message });
            }
        }

        return {
            success: errors.length === 0,
            processed,
            daysAffected,
            resolvedDayEntries,
            errors: errors.length > 0 ? errors : undefined
        };
    },

    // Helper method for removing day entries
    async processRemoveDayEntries(updateSuggestion, accommodationId, userId, tripId) {
        const { days } = updateSuggestion;
        let processed = 0;
        let errors = [];
        const daysAffected = [];
        const resolvedDayIds = []; // for registry archive (Issue 13)

        for (const day of days) {
            try {
                // Check if 'day' is a date string (YYYY-MM-DD) or already a dayId
                let dayId = day;

                // If it looks like a date string, convert to dayId by looking up the day
                if (typeof day === 'string' && day.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    console.log(`🏨 [SuggestionService] processRemoveDayEntries: Converting date ${day} to dayId`);
                    const existingDay = await daysService.getDayByDate(userId, tripId, day);
                    if (existingDay) {
                        dayId = existingDay.id;
                        console.log(`🏨 [SuggestionService] processRemoveDayEntries: Found dayId ${dayId} for date ${day}`);
                    } else {
                        console.warn(`🏨 [SuggestionService] processRemoveDayEntries: No day found for date ${day}, skipping`);
                        errors.push({ date: day, error: `No day entry exists for date ${day}` });
                        continue;
                    }
                }

                const result = await daysService.removeAccommodationContentBlocks(
                    userId, tripId, dayId, accommodationId
                );

                if (result.success) {
                    processed++;
                    daysAffected.push(day); // Keep original day identifier for tracking
                    resolvedDayIds.push(dayId);
                } else {
                    errors.push({ date: day, error: result.error });
                }
            } catch (error) {
                console.error(`🏨 [SuggestionService] processRemoveDayEntries: Error processing day ${day}:`, error);
                errors.push({ date: day, error: error.message });
            }
        }

        return {
            success: errors.length === 0,
            processed,
            daysAffected,
            resolvedDayIds,
            errors: errors.length > 0 ? errors : undefined
        };
    },

    // Helper method for updating day entries
    async processUpdateDayEntries(updateSuggestion, accommodationId, accommodationDetails, userId, tripId) {
        const { days, content } = updateSuggestion;
        let processed = 0;
        let errors = [];
        const daysAffected = [];

        for (const day of days) {
            try {
                // Check if 'day' is a date string (YYYY-MM-DD) or already a dayId
                let dayId = day;

                // If it looks like a date string, convert to dayId by looking up the day
                if (typeof day === 'string' && day.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    console.log(`🏨 [SuggestionService] processUpdateDayEntries: Converting date ${day} to dayId`);
                    const existingDay = await daysService.getDayByDate(userId, tripId, day);
                    if (existingDay) {
                        dayId = existingDay.id;
                        console.log(`🏨 [SuggestionService] processUpdateDayEntries: Found dayId ${dayId} for date ${day}`);
                    } else {
                        console.warn(`🏨 [SuggestionService] processUpdateDayEntries: No day found for date ${day}, skipping`);
                        errors.push({ date: day, error: `No day entry exists for date ${day}` });
                        continue;
                    }
                }

                // First remove old content blocks, then add new content blocks
                const removeResult = await daysService.removeAccommodationContentBlocks(
                    userId, tripId, dayId, accommodationId
                );

                if (removeResult.success) {
                    // Create structured accommodation content block
                    const dateRange = {
                        startDate: accommodationDetails?.checkInDate,
                        endDate: accommodationDetails?.checkOutDate
                    };

                    const accommodationContentBlock = {
                        id: `acc_block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                        type: 'accommodation_reference',
                        accommodationId: accommodationId,
                        accommodationName: accommodationDetails?.name || 'Accommodation',
                        context: this.determineDayContext(day, dateRange),
                        createdAt: new Date().toISOString()
                    };

                    const addResult = await daysService.addAccommodationContentBlock(
                        userId, tripId, dayId, accommodationContentBlock
                    );

                    if (addResult.success) {
                        processed++;
                        daysAffected.push(day); // Keep original day identifier for tracking
                    } else {
                        errors.push({ date: day, error: addResult.error });
                    }
                } else {
                    errors.push({ date: day, error: removeResult.error });
                }
            } catch (error) {
                console.error(`🏨 [SuggestionService] processUpdateDayEntries: Error processing day ${day}:`, error);
                errors.push({ date: day, error: error.message });
            }
        }

        return {
            success: errors.length === 0,
            processed,
            daysAffected,
            errors: errors.length > 0 ? errors : undefined
        };
    },

    // Helper method for publishing events based on change type
    publishDateChangeEvents(changeAnalysis, accommodationId, userId, tripId, daysAffected) {
        try {
            const baseEventData = {
                userId,
                tripId,
                accommodationId,
                timestamp: new Date().toISOString(),
                eventId: `evt_${Date.now()}`
            };

            // Publish specific events based on change type
            switch (changeAnalysis.type) {
                case 'DATES_ADDED':
                case 'EXPANSION':
                    eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_DAY_LINK_CREATED, {
                        ...baseEventData,
                        daysAffected,
                        changeType: changeAnalysis.type
                    });
                    break;

                case 'DATES_REMOVED':
                case 'CONTRACTION':
                    eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_DAY_LINK_REMOVED, {
                        ...baseEventData,
                        daysAffected,
                        changeType: changeAnalysis.type
                    });
                    break;

                case 'SHIFT_SAME_DURATION':
                case 'SHIFT_DIFFERENT_DURATION':
                case 'COMPLETE_SHIFT':
                    // For shifts, publish both removal and creation events
                    if (changeAnalysis.removedDays?.length > 0) {
                        eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_DAY_LINK_REMOVED, {
                            ...baseEventData,
                            daysAffected: changeAnalysis.removedDays,
                            changeType: 'SHIFT_REMOVAL'
                        });
                    }

                    if (changeAnalysis.addedDays?.length > 0) {
                        eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_DAY_LINK_CREATED, {
                            ...baseEventData,
                            daysAffected: changeAnalysis.addedDays,
                            changeType: 'SHIFT_ADDITION'
                        });
                    }
                    break;
            }
        } catch (error) {
            console.warn('🚨 [SuggestionService] Error publishing date change events:', error.message);
        }
    },

    formatAccommodationContentForDay(accommodation, date) {
        const name = accommodation.details?.name || 'Accommodation';
        const city = accommodation.details?.city;
        const country = accommodation.details?.country;
        const checkInDate = accommodation.details?.checkInDate;
        const checkOutDate = accommodation.details?.checkOutDate;

        // Build location string
        let location = '';
        if (city && country) {
            location = `${city}, ${country}`;
        } else if (city) {
            location = city;
        }

        // Base content format
        let content = `Stay at ${name}`;
        if (location) {
            content += `: ${location}`;
        }

        // Add context if dates are available
        if (checkInDate && checkOutDate) {
            if (date === checkInDate && date === checkOutDate) {
                // Single day stay - no context needed
                return content;
            } else if (date === checkInDate) {
                content += ' (Check-in)';
            } else if (date === checkOutDate) {
                content += ' (Check-out)';
            } else {
                content += ' (Staying)';
            }
        } else if (checkInDate && date === checkInDate) {
            content += ' (Check-in)';
        }

        return content;
    },

    async applySuggestion(suggestion) {
        console.log('SuggestionService: applySuggestion called:', suggestion.id, suggestion.suggestionType);

        try {
            const { suggestionType } = suggestion;

            switch (suggestionType) {
                case 'DAY_ENTRIES_FOR_ACCOMMODATION':
                    await this.handleDayEntriesForAccommodation(suggestion);
                    break;

                case 'DAY_UPDATES_FOR_CHANGED_ACCOMMODATION':
                case 'ACCOMMODATION_DATE_CHANGE_UPDATE':
                    await this.handleDayUpdatesForChangedAccommodation(suggestion);
                    break;



                case 'ACCOMMODATION_DELETION_CLEANUP':
                    await this.handleAccommodationDeletionCleanup(suggestion);
                    break;

                case 'PLACE_LINK_OR_CREATE_FROM_ACCOMMODATION':
                case 'PLACE_LINK_OR_CREATE_FROM_CITY_CHANGE':
                    await this.handlePlaceCreationOrLinking(suggestion);
                    break;

                case 'PLACE_ORPHANED_NOTIFICATION':
                    // Informational only - no action needed
                    console.log('SuggestionService: Processed orphaned place notification - no action required');
                    break;

                case 'MISSING_DAYS_NOTIFICATION':
                    // Informational notification - user needs to create day entries first
                    console.log('SuggestionService: Processed missing days notification - no automatic action taken');
                    break;

                default:
                    console.warn(`🚨 [SuggestionService] applySuggestion - Unknown or unhandled suggestion type: ${suggestionType}`);
            }
        } catch (error) {
            console.error('🚨 [SuggestionService] Error applying suggestion:', error);
            throw error;
        }
    },

    async handlePlaceCreationOrLinking(suggestion) {
        console.log('SuggestionService: handlePlaceCreationOrLinking called:', suggestion.id);

        try {
            const { data } = suggestion;
            const { userId, tripId, accommodationId, needsCreation } = data;
            let placeId = null;
            let placeData = null;

            if (needsCreation) {
                // Create new place
                const placeCreationData = data.placeCreationData;
                if (!placeCreationData) {
                    throw new Error('No place creation data available');
                }

                placeId = await placesService.createPlace(userId, tripId, placeCreationData);
                placeData = placeCreationData; // Use the creation data
                console.log(`🏨 [SuggestionService] handlePlaceCreationOrLinking: Created new place: ${placeId} for accommodation: ${accommodationId}`);
            } else {
                // Link to existing place
                placeId = data.existingPlaceId;
                if (!placeId) {
                    throw new Error('No existing place ID available for linking');
                }

                // Fetch the existing place data to get its name/fullName for city update
                try {
                    // getPlaceById is imported as ESM import at top of file
                    placeData = await getPlaceById(userId, tripId, placeId);
                    if (!placeData) {
                        console.warn(`🏨 [SuggestionService] handlePlaceCreationOrLinking: Could not fetch place data for ID ${placeId}, proceeding with place linking only`);
                    }
                } catch (fetchError) {
                    console.warn(`🏨 [SuggestionService] handlePlaceCreationOrLinking: Error fetching place data for ${placeId}:`, fetchError.message);
                    // Continue with place linking even if we can't fetch place data
                }

                console.log(`🏨 [SuggestionService] handlePlaceCreationOrLinking: Linking to existing place: ${placeId} for accommodation: ${accommodationId}`);
            }

            if (!placeId) {
                throw new Error('No place ID determined for linking');
            }

            // Link the accommodation to the place
            await accommodationService.updateAccommodationPlaceReference(userId, tripId, accommodationId, placeId);
            console.log(`🏨 [SuggestionService] handlePlaceCreationOrLinking: Successfully linked accommodation ${accommodationId} to place ${placeId}`);

            // Update accommodation city details to match the selected place
            if (placeData) {
                try {
                    // Get current accommodation data
                    const currentAccommodation = await accommodationService.getAccommodationById(userId, tripId, accommodationId);
                    if (currentAccommodation) {
                        // Determine the best city name from place data
                        // Priority: fullName > name > existing city (fallback)
                        const newCityName = placeData.fullName || placeData.name || currentAccommodation.details?.city;

                        if (newCityName && newCityName !== currentAccommodation.details?.city) {
                            console.log(`🏨 [SuggestionService] handlePlaceCreationOrLinking: Updating accommodation city from "${currentAccommodation.details?.city}" to "${newCityName}"`);

                            // Create updated details with new city
                            const updatedDetails = {
                                ...currentAccommodation.details,
                                city: newCityName
                            };

                            // Generate new summary using the accommodation type's createSummary function
                            let newSummary = currentAccommodation.summary;
                            try {
                                // ACCOMMODATION_TYPES is imported as ESM import at top of file
                                const typeConfig = ACCOMMODATION_TYPES[currentAccommodation.type];
                                if (typeConfig && typeConfig.createSummary) {
                                    newSummary = typeConfig.createSummary(updatedDetails);
                                    console.log(`🏨 [SuggestionService] handlePlaceCreationOrLinking: Generated new summary: "${newSummary}"`);
                                } else {
                                    // Fallback summary generation if type config not available
                                    const accommodationName = updatedDetails.name || 'Accommodation';
                                    newSummary = `${accommodationName}: ${newCityName}`;
                                    console.log(`🏨 [SuggestionService] handlePlaceCreationOrLinking: Generated fallback summary: "${newSummary}"`);
                                }
                            } catch (typeImportError) {
                                console.warn(`🏨 [SuggestionService] handlePlaceCreationOrLinking: Could not import ACCOMMODATION_TYPES, using fallback summary generation`);
                                const accommodationName = updatedDetails.name || 'Accommodation';
                                newSummary = `${accommodationName}: ${newCityName}`;
                            }

                            // Update the accommodation with the new city and summary information
                            const updatedAccommodationData = {
                                ...currentAccommodation,
                                details: updatedDetails,
                                summary: newSummary,
                                updatedAt: new Date().toISOString()
                            };

                            // Use updateAccommodation to ensure proper validation and events
                            await accommodationService.updateAccommodation(userId, tripId, accommodationId, updatedAccommodationData);
                            console.log(`🏨 [SuggestionService] handlePlaceCreationOrLinking: Successfully updated accommodation city and summary to match linked place`);
                        } else {
                            console.log(`🏨 [SuggestionService] handlePlaceCreationOrLinking: No city update needed - place name matches current city or no valid place name found`);
                        }
                    }
                } catch (updateError) {
                    console.error(`🏨 [SuggestionService] handlePlaceCreationOrLinking: Error updating accommodation city details:`, updateError);
                    // Don't throw - place linking was successful, city update is a bonus
                }
            } else {
                console.log(`🏨 [SuggestionService] handlePlaceCreationOrLinking: No place data available for city update`);
            }

        } catch (error) {
            console.error('🚨 [SuggestionService] Error in handlePlaceCreationOrLinking:', error);
            throw error;
        }
    },

    /**
     * Handle accommodation deletion cleanup by removing all day entries that reference the deleted accommodation
     * @param {Object} suggestion - The suggestion object containing deletion cleanup data
     * @returns {Promise<Object>} - Result of the cleanup operation
     */
    async handleAccommodationDeletionCleanup(suggestion) {
        console.log('🗑️ [SuggestionService] handleAccommodationDeletionCleanup: Starting cleanup', {
            suggestionId: suggestion.id,
            ruleName: suggestion.ruleName,
            accommodationId: suggestion.data?.accommodationId,
            affectedDayCount: suggestion.data?.affectedDayCount
        });

        try {
            const { accommodationId, tripId, userId, accommodationName, cleanupNeeded } = suggestion.data;

            if (!cleanupNeeded) {
                console.log('ℹ️ [SuggestionService] handleAccommodationDeletionCleanup: No cleanup needed');
                return {
                    success: true,
                    processed: 0,
                    message: 'No cleanup was needed'
                };
            }

            // For deletion cleanup, we use a different approach than date changes:
            // Instead of trying to determine the date range (accommodation is deleted), 
            // we iterate through ALL days and use the proven daysService.removeAccommodationContentBlocks
            // which can remove by accommodation ID without needing dates

            console.log('🔄 [SuggestionService] handleAccommodationDeletionCleanup: Using proven day-by-day cleanup approach');

            // Get all days for this trip  
            const allDays = await daysService.fetchDays(userId, tripId);

            let totalCleaned = 0;
            let cleanedDays = [];
            let errors = [];

            // Process each day using the same method that works for date changes
            for (const day of allDays) {
                try {
                    const result = await daysService.removeAccommodationContentBlocks(
                        userId, tripId, day.id, accommodationId
                    );

                    if (result.removedCount > 0) {
                        totalCleaned += result.removedCount;
                        cleanedDays.push({
                            dayId: day.id,
                            date: day.date,
                            title: day.title || `Day ${day.order + 1}`,
                            removedCount: result.removedCount
                        });

                        console.log(`✅ [SuggestionService] handleAccommodationDeletionCleanup: Cleaned ${result.removedCount} references from day ${day.id}`);
                    }
                } catch (dayError) {
                    console.error(`❌ [SuggestionService] handleAccommodationDeletionCleanup: Error cleaning day ${day.id}:`, dayError);
                    errors.push({
                        dayId: day.id,
                        date: day.date,
                        error: dayError.message
                    });
                }
            }

            // Publish cleanup completion event for user notification
            eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_DAYS_CLEANUP_COMPLETED, {
                accommodationId,
                accommodationName,
                tripId,
                userId,
                totalCleaned,
                cleanedDays,
                success: errors.length === 0,
                timestamp: new Date().toISOString()
            });

            const result = {
                success: errors.length === 0,
                processed: totalCleaned,
                cleanedDays,
                totalDaysProcessed: allDays.length,
                accommodationName
            };

            if (errors.length > 0) {
                result.partialSuccess = totalCleaned > 0;
                result.errors = errors;
            }

            console.log('✅ [SuggestionService] handleAccommodationDeletionCleanup: Cleanup completed', {
                accommodationId,
                totalCleaned,
                cleanedDaysCount: cleanedDays.length,
                errorsCount: errors.length
            });

            return result;

        } catch (error) {
            console.error('❌ [SuggestionService] handleAccommodationDeletionCleanup: Critical error:', error);
            return {
                success: false,
                error: error.message,
                processed: 0
            };
        }
    },

    // New method to handle responses from immediate suggestion popups via event
    async handleUserResponseEvent(eventPayload) {
        const { suggestionId, choice, selectedPlaceId, status: userProvidedStatus } = eventPayload;
        console.log(`🏨 [SuggestionService] handleUserResponseEvent: Received USER_RESPONDED_TO_SUGGESTION for ID ${suggestionId}, choice: ${choice}`);

        const suggestion = this.activeImmediateSuggestions[suggestionId];

        if (!suggestion) {
            console.warn(`🏨 [SuggestionService] handleUserResponseEvent: Suggestion with ID ${suggestionId} not found in activeImmediateSuggestions. It might have been already processed or an invalid ID was sent.`);
            return;
        }

        // Remove from active cache once retrieved, regardless of outcome, to prevent reprocessing.
        delete this.activeImmediateSuggestions[suggestionId];

        if (choice === 'accept') {
            suggestion.status = 'accepted';
            console.log(`🏨 [SuggestionService] handleUserResponseEvent: Immediate suggestion ${suggestionId} accepted by user. Attempting to apply.`);

            try {
                // Handle disambiguation choice
                if (suggestion.suggestionType === 'PLACE_DISAMBIGUATION_REQUIRED') {
                    await this.processDisambiguationChoice(suggestion, selectedPlaceId);
                    suggestion.status = 'applied';
                } else {
                    await this.applySuggestion(suggestion);
                    suggestion.status = 'applied';
                }

                // Mark suggestion as completed for automatic cleanup
                this.markSuggestionCompleted(suggestionId, suggestion.status);

                // Publish suggestion processed event for UI updates
                eventBridgeService.publish(EVENT_TYPES.SUGGESTION_PROCESSED, {
                    suggestionId,
                    status: suggestion.status,
                    suggestionType: suggestion.suggestionType,
                    data: suggestion.data
                });
                console.log(`🏨 [SuggestionService] handleUserResponseEvent: Published SUGGESTION_PROCESSED event for ${suggestionId}`);

            } catch (error) {
                console.error(`🚨 [SuggestionService] handleUserResponseEvent: Error applying accepted immediate suggestion ${suggestionId}:`, error);
                suggestion.status = 'failed';

                // Mark failed suggestion as completed for automatic cleanup
                this.markSuggestionCompleted(suggestionId, suggestion.status);

                // Publish failure event
                eventBridgeService.publish(EVENT_TYPES.SUGGESTION_PROCESSED, {
                    suggestionId,
                    status: suggestion.status,
                    suggestionType: suggestion.suggestionType,
                    error: error.message
                });
            }
        } else { // 'reject' or any other non-accept choice
            suggestion.status = userProvidedStatus || 'rejected';
            console.log(`🏨 [SuggestionService] handleUserResponseEvent: Immediate suggestion ${suggestionId} processed with status: ${suggestion.status}.`);

            // Mark suggestion as completed for automatic cleanup
            this.markSuggestionCompleted(suggestionId, suggestion.status);

            // Publish suggestion processed event for UI updates
            eventBridgeService.publish(EVENT_TYPES.SUGGESTION_PROCESSED, {
                suggestionId,
                status: suggestion.status,
                suggestionType: suggestion.suggestionType,
                data: suggestion.data
            });

            // Check if this was a city change suggestion that was rejected, and if there's an old place to clean up
            if (suggestion.suggestionType === 'PLACE_LINK_OR_CREATE_FROM_CITY_CHANGE' && suggestion.data.oldPlaceReference) {
                console.log(`🏨 [SuggestionService] handleUserResponseEvent: City change suggestion was rejected, but still need to check old place cleanup for: ${suggestion.data.oldPlaceReference.placeId}`);
                setTimeout(() => {
                    this.generateOldPlaceCleanupSuggestion(suggestion.data);
                }, 1000); // Short delay to ensure any database changes have settled
            }
        }
    },

    async processDisambiguationChoice(suggestion, selectedPlaceId) {
        console.log(`🏨 [SuggestionService] processDisambiguationChoice: Processing disambiguation choice for ${suggestion.id}, selectedPlaceId: ${selectedPlaceId}`);

        try {
            const { data } = suggestion;
            const { userId, tripId, accommodationId } = data;
            let placeId = null;
            let placeData = null;

            if (selectedPlaceId === 'create_new') {
                // Create new place
                const newPlaceOption = data.newPlaceOption;
                if (!newPlaceOption) {
                    throw new Error('No new place option available for creation');
                }

                placeId = await placesService.createPlace(userId, tripId, newPlaceOption);
                placeData = newPlaceOption; // Use the creation data
                console.log(`🏨 [SuggestionService] processDisambiguationChoice: Created new place: ${placeId} for accommodation: ${accommodationId}`);
            } else {
                // Link to existing place
                placeId = selectedPlaceId;

                // Fetch the existing place data to get its name/fullName for city update
                try {
                    // getPlaceById is imported as ESM import at top of file
                    placeData = await getPlaceById(userId, tripId, placeId);
                    if (!placeData) {
                        console.warn(`🏨 [SuggestionService] processDisambiguationChoice: Could not fetch place data for ID ${placeId}, proceeding with place linking only`);
                    }
                } catch (fetchError) {
                    console.warn(`🏨 [SuggestionService] processDisambiguationChoice: Error fetching place data for ${placeId}:`, fetchError.message);
                    // Continue with place linking even if we can't fetch place data
                }

                console.log(`🏨 [SuggestionService] processDisambiguationChoice: Linking to existing place: ${placeId} for accommodation: ${accommodationId}`);
            }

            if (!placeId) {
                throw new Error('No place ID determined for linking');
            }

            // Link the accommodation to the place
            await accommodationService.updateAccommodationPlaceReference(userId, tripId, accommodationId, placeId);
            console.log(`🏨 [SuggestionService] processDisambiguationChoice: Successfully linked accommodation ${accommodationId} to place ${placeId}`);

            // Update accommodation city details to match the selected place
            if (placeData) {
                try {
                    // Get current accommodation data
                    const currentAccommodation = await accommodationService.getAccommodationById(userId, tripId, accommodationId);
                    if (currentAccommodation) {
                        // Determine the best city name from place data
                        // Priority: fullName > name > existing city (fallback)
                        const newCityName = placeData.fullName || placeData.name || currentAccommodation.details?.city;

                        if (newCityName && newCityName !== currentAccommodation.details?.city) {
                            console.log(`🏨 [SuggestionService] processDisambiguationChoice: Updating accommodation city from "${currentAccommodation.details?.city}" to "${newCityName}"`);

                            // Create updated details with new city
                            const updatedDetails = {
                                ...currentAccommodation.details,
                                city: newCityName
                            };

                            // Generate new summary using the accommodation type's createSummary function
                            let newSummary = currentAccommodation.summary;
                            try {
                                // ACCOMMODATION_TYPES is imported as ESM import at top of file
                                const typeConfig = ACCOMMODATION_TYPES[currentAccommodation.type];
                                if (typeConfig && typeConfig.createSummary) {
                                    newSummary = typeConfig.createSummary(updatedDetails);
                                    console.log(`🏨 [SuggestionService] processDisambiguationChoice: Generated new summary: "${newSummary}"`);
                                } else {
                                    // Fallback summary generation if type config not available
                                    const accommodationName = updatedDetails.name || 'Accommodation';
                                    newSummary = `${accommodationName}: ${newCityName}`;
                                    console.log(`🏨 [SuggestionService] processDisambiguationChoice: Generated fallback summary: "${newSummary}"`);
                                }
                            } catch (typeImportError) {
                                console.warn(`🏨 [SuggestionService] processDisambiguationChoice: Could not import ACCOMMODATION_TYPES, using fallback summary generation`);
                                const accommodationName = updatedDetails.name || 'Accommodation';
                                newSummary = `${accommodationName}: ${newCityName}`;
                            }

                            // Update the accommodation with the new city and summary information
                            const updatedAccommodationData = {
                                ...currentAccommodation,
                                details: updatedDetails,
                                summary: newSummary,
                                updatedAt: new Date().toISOString()
                            };

                            // Use updateAccommodation to ensure proper validation and events
                            await accommodationService.updateAccommodation(userId, tripId, accommodationId, updatedAccommodationData);
                            console.log(`🏨 [SuggestionService] processDisambiguationChoice: Successfully updated accommodation city and summary to match linked place`);
                        } else {
                            console.log(`🏨 [SuggestionService] processDisambiguationChoice: No city update needed - place name matches current city or no valid place name found`);
                        }
                    }
                } catch (updateError) {
                    console.error(`🏨 [SuggestionService] processDisambiguationChoice: Error updating accommodation city details:`, updateError);
                    // Don't throw - place linking was successful, city update is a bonus
                }
            } else {
                console.log(`🏨 [SuggestionService] processDisambiguationChoice: No place data available for city update`);
            }

        } catch (error) {
            console.error('🚨 [SuggestionService] processDisambiguationChoice: Error processing disambiguation choice:', error);
            throw error;
        }
    },

    async generateOldPlaceCleanupSuggestion(suggestionData) {
        // This method would generate suggestions for cleaning up old places that are no longer referenced
        // Implementation would depend on the specific cleanup rules
        console.log('🏨 [SuggestionService] generateOldPlaceCleanupSuggestion: Starting cleanup operation', {
            suggestionId: suggestionData.id,
            accommodationId: suggestionData.data.accommodationId,
            accommodationName: suggestionData.data.accommodation?.details?.name || 'Unknown',
            contentBlocksCount: suggestionData.data.contentBlocksToClean?.length || 0
        });
        // TODO: Implement old place cleanup suggestion generation if needed
    },

    getPendingSuggestions() {
        return this.pendingSuggestions;
    },

    // Debugging methods for cache management
    debugActiveCache() {
        console.log('🔍 [SuggestionService] Debug: Active immediate suggestions cache:');
        const activeIds = Object.keys(this.activeImmediateSuggestions);

        if (activeIds.length === 0) {
            console.log('✅ Cache is empty');
            return { cacheSize: 0, suggestions: [] };
        }

        console.log(`📊 Cache contains ${activeIds.length} suggestions:`);

        const debugInfo = activeIds.map(id => {
            const suggestion = this.activeImmediateSuggestions[id];
            const ageMs = Date.now() - new Date(suggestion.createdAt).getTime();
            const ageMinutes = Math.round(ageMs / 1000 / 60 * 10) / 10; // Round to 1 decimal

            console.log(`  • ${id}: ${suggestion.ruleName} (${suggestion.suggestionType}) - Status: ${suggestion.status} - Age: ${ageMinutes}min`);
            console.log(`    Data: accommodationId=${suggestion.data?.accommodationId}, tripId=${suggestion.data?.tripId}`);

            return {
                id,
                ruleName: suggestion.ruleName,
                suggestionType: suggestion.suggestionType,
                status: suggestion.status,
                accommodationId: suggestion.data?.accommodationId,
                ageMinutes,
                createdAt: suggestion.createdAt
            };
        });

        return { cacheSize: activeIds.length, suggestions: debugInfo };
    },

    clearStaleActiveSuggestions(maxAgeMinutes = 30) {
        console.log(`🧹 [SuggestionService] Clearing stale suggestions older than ${maxAgeMinutes} minutes...`);

        const cutoffTime = Date.now() - (maxAgeMinutes * 60 * 1000);
        let clearedCount = 0;

        Object.keys(this.activeImmediateSuggestions).forEach(id => {
            const suggestion = this.activeImmediateSuggestions[id];
            const createdTime = new Date(suggestion.createdAt).getTime();

            if (createdTime < cutoffTime) {
                console.log(`🗑️ Clearing stale suggestion: ${id} (${suggestion.ruleName}) - Age: ${Math.round((Date.now() - createdTime) / 1000 / 60)}min`);
                delete this.activeImmediateSuggestions[id];
                clearedCount++;
            }
        });

        console.log(`✅ Cleared ${clearedCount} stale suggestions. Cache now contains ${Object.keys(this.activeImmediateSuggestions).length} suggestions.`);
        return clearedCount;
    },

    clearAllActiveSuggestions() {
        console.log('🧹 [SuggestionService] Clearing ALL active suggestions cache...');
        const clearedCount = Object.keys(this.activeImmediateSuggestions).length;
        this.activeImmediateSuggestions = {};
        console.log(`✅ Cleared ${clearedCount} suggestions. Cache is now empty.`);
        return clearedCount;
    },

    // Development helper function to force clear cache for testing
    forceRefreshSuggestionCache() {
        console.log('🔄 [SuggestionService] Force refreshing suggestion cache...');
        const clearedCount = this.clearAllActiveSuggestions();
        this.automaticCacheCleanup();
        console.log(`✅ Cache refreshed. Previously had ${clearedCount} suggestions.`);
        return clearedCount;
    },

    // Method to help debug accommodation-specific suggestions
    debugAccommodationSuggestions(accommodationId) {
        console.log(`🏨 [SuggestionService] Debug: Suggestions for accommodation ${accommodationId}:`);

        const accommodationSuggestions = Object.values(this.activeImmediateSuggestions)
            .filter(s => s.data?.accommodationId === accommodationId);

        if (accommodationSuggestions.length === 0) {
            console.log('✅ No active suggestions for this accommodation');
            return [];
        }

        console.log(`📊 Found ${accommodationSuggestions.length} active suggestions:`);
        accommodationSuggestions.forEach(suggestion => {
            const ageMs = Date.now() - new Date(suggestion.createdAt).getTime();
            const ageMinutes = Math.round(ageMs / 1000 / 60 * 10) / 10;
            console.log(`  • ${suggestion.id}: ${suggestion.ruleName} - Status: ${suggestion.status} - Age: ${ageMinutes}min`);
        });

        return accommodationSuggestions;
    },

    // AUTOMATIC CACHE MANAGEMENT - These methods run automatically to keep the system robust
    automaticCacheCleanup() {
        console.log('🧹 [SuggestionService] Running automatic cache cleanup...');

        const maxAgeMinutes = 15; // Clear suggestions older than 15 minutes
        const cutoffTime = Date.now() - (maxAgeMinutes * 60 * 1000);
        let clearedCount = 0;

        Object.keys(this.activeImmediateSuggestions).forEach(id => {
            const suggestion = this.activeImmediateSuggestions[id];
            const createdTime = new Date(suggestion.createdAt).getTime();

            // Clear stale suggestions that are old AND not currently being processed
            if (createdTime < cutoffTime && suggestion.status !== 'applying') {
                console.log(`🗑️ Auto-clearing stale suggestion: ${id} (${suggestion.ruleName}) - Age: ${Math.round((Date.now() - createdTime) / 1000 / 60)}min`);
                delete this.activeImmediateSuggestions[id];
                clearedCount++;
            }
        });

        if (clearedCount > 0) {
            console.log(`✅ Auto-cleared ${clearedCount} stale suggestions. Cache now contains ${Object.keys(this.activeImmediateSuggestions).length} suggestions.`);
        }

        return clearedCount;
    },

    clearStaleAccommodationSuggestions(accommodationId, maxAgeMinutes = 10) {
        console.log(`🏨 [SuggestionService] Clearing stale suggestions for accommodation ${accommodationId} (maxAge: ${maxAgeMinutes} minutes)...`);

        const cutoffTime = Date.now() - (maxAgeMinutes * 60 * 1000);
        let clearedCount = 0;

        Object.keys(this.activeImmediateSuggestions).forEach(id => {
            const suggestion = this.activeImmediateSuggestions[id];

            // Clear suggestions for this accommodation
            if (suggestion.data?.accommodationId === accommodationId) {
                const createdTime = new Date(suggestion.createdAt).getTime();
                const isStale = createdTime < cutoffTime;
                const isProcessed = ['applied', 'accepted', 'rejected'].includes(suggestion.status);
                const forceCleanAll = maxAgeMinutes === 0; // Aggressive cleanup mode

                if (isStale || isProcessed || forceCleanAll) {
                    const reason = forceCleanAll ? 'aggressive cleanup' : (isStale ? 'stale' : 'processed');
                    console.log(`🗑️ Clearing ${reason} suggestion for accommodation: ${id} (${suggestion.ruleName}) - Status: ${suggestion.status}`);
                    delete this.activeImmediateSuggestions[id];
                    clearedCount++;
                }
            }
        });

        if (clearedCount > 0) {
            console.log(`✅ Cleared ${clearedCount} suggestions for accommodation ${accommodationId}.`);
        }

        return clearedCount;
    },

    // Enhanced cleanup on suggestion completion
    markSuggestionCompleted(suggestionId, status = 'applied') {
        if (this.activeImmediateSuggestions[suggestionId]) {
            this.activeImmediateSuggestions[suggestionId].status = status;
            this.activeImmediateSuggestions[suggestionId].completedAt = new Date().toISOString();

            // Auto-remove completed suggestions after a short delay to prevent immediate duplicates
            setTimeout(() => {
                if (this.activeImmediateSuggestions[suggestionId]) {
                    console.log(`🧹 [SuggestionService] Auto-removing completed suggestion: ${suggestionId}`);
                    delete this.activeImmediateSuggestions[suggestionId];
                }
            }, 3000); // Remove after 3 seconds
        }
    },

    /**
     * Handle accommodation deletion cleanup
     * Removes all content blocks that reference the deleted accommodation
     */
    async handleAccommodationDeletionCleanup(suggestion) {
        const { accommodationId, tripId, userId, accommodationName, affectedDayCount, potentiallyAffectedDays, cleanupNeeded } = suggestion.data;

        console.log('🗑️ [SuggestionService] handleAccommodationDeletionCleanup: Starting cleanup operation', {
            suggestionId: suggestion.id,
            accommodationId,
            accommodationName,
            affectedDayCount,
            cleanupNeeded
        });

        if (!cleanupNeeded) {
            console.log('ℹ️ [SuggestionService] handleAccommodationDeletionCleanup: No cleanup needed');
            return { success: true, processed: 0, message: 'No cleanup was needed' };
        }

        try {
            let totalCleaned = 0;
            let cleanedDays = [];

            console.log('🔍 [SuggestionService] handleAccommodationDeletionCleanup: Processing cleanup for affected days', {
                accommodationId,
                tripId,
                userId,
                daysToProcess: potentiallyAffectedDays?.length || 0
            });

            // Process each affected day using the proven daysService.removeAccommodationContentBlocks method
            for (const dayInfo of potentiallyAffectedDays || []) {
                try {
                    console.log('🧹 [SuggestionService] handleAccommodationDeletionCleanup: Cleaning day', {
                        dayId: dayInfo.dayId,
                        date: dayInfo.date,
                        title: dayInfo.title
                    });

                    const result = await daysService.removeAccommodationContentBlocks(
                        userId, tripId, dayInfo.dayId, accommodationId
                    );

                    if (result.removedCount > 0) {
                        totalCleaned += result.removedCount;
                        cleanedDays.push({
                            dayId: dayInfo.dayId,
                            date: dayInfo.date,
                            title: dayInfo.title,
                            removedCount: result.removedCount
                        });

                        console.log('✅ [SuggestionService] handleAccommodationDeletionCleanup: Day cleaned successfully', {
                            dayId: dayInfo.dayId,
                            removedCount: result.removedCount
                        });
                    } else {
                        console.log('ℹ️ [SuggestionService] handleAccommodationDeletionCleanup: No content blocks found to remove', {
                            dayId: dayInfo.dayId
                        });
                    }
                } catch (dayError) {
                    console.error('❌ [SuggestionService] handleAccommodationDeletionCleanup: Failed to clean day', {
                        dayId: dayInfo.dayId,
                        error: dayError.message
                    });
                }
            }

            console.log('✅ [SuggestionService] handleAccommodationDeletionCleanup: Cleanup completed successfully', {
                accommodationId,
                accommodationName,
                totalCleaned,
                cleanedDaysCount: cleanedDays.length
            });

            return {
                success: true,
                processed: totalCleaned,
                cleanedDays,
                accommodationName
            };

        } catch (error) {
            console.error('🚨 [SuggestionService] handleAccommodationDeletionCleanup: Operation failed', {
                suggestionId: suggestion.id,
                accommodationId,
                error: error.message,
                stack: error.stack
            });

            throw error;
        }
    },


}; 